import { useQuery } from '@tanstack/react-query';
import { Bar } from 'react-chartjs-2';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import { Box, Card, CardContent, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { AssignmentLate, BadgeOutlined, Factory, Payments } from '@mui/icons-material';
import { api } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import { formatDate } from '../utils/date.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const cards = [
  ['Today’s production', 'TodayProduction', Factory, '#176b5b'],
  ['Today’s attendance', 'TodayAttendance', BadgeOutlined, '#4177b8'],
  ['Pending orders', 'PendingOrders', AssignmentLate, '#d18825'],
  ['Cost summary', 'TodayCost', Payments, '#8c5fb3']
];

export default function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get('/dashboard')).data,
    refetchInterval: 60000
  });
  if (isLoading) return <Box textAlign="center" py={10}><CircularProgress /></Box>;
  if (error) return <Typography color="error">{error.response?.data?.message || 'Dashboard unavailable'}</Typography>;

  const chartData = {
    labels: data.productionTrend.map((item) => formatDate(item.ProductionDate)),
    datasets: [{ label: 'Output quantity', data: data.productionTrend.map((item) => item.Quantity), backgroundColor: '#2c8f7b', borderRadius: 7 }]
  };
  return (
    <>
      <PageHeader title="Operations Overview" subtitle={`Live factory performance for ${formatDate(new Date())}`} />
      <Grid container spacing={2.5}>
        {cards.map(([title, field, Icon, color]) => (
          <Grid key={field} size={{ xs: 12, sm: 6, md: 3 }}><Card><CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box><Typography color="text.secondary">{title}</Typography><Typography variant="h4" mt={1}>
                {field === 'TodayCost' ? `Rs ${Number(data.summary[field]).toLocaleString()}` : Number(data.summary[field]).toLocaleString()}
              </Typography></Box>
              <Box sx={{ bgcolor: `${color}18`, color, p: 1.2, borderRadius: 2 }}><Icon /></Box>
            </Stack>
          </CardContent></Card></Grid>
        ))}
        <Grid size={{ xs: 12, lg: 8 }}><Card><CardContent>
          <Typography variant="h6" mb={2}>Seven-day production</Typography>
          <Box height={310}><Bar data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} /></Box>
        </CardContent></Card></Grid>
        <Grid size={{ xs: 12, lg: 4 }}><Card sx={{ height: '100%' }}><CardContent>
          <Typography variant="h6" mb={2}>Department status</Typography>
          <Stack spacing={1.5}>{data.departments.map((item) => (
            <Box key={item.Department} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>{item.Department}</Typography><Chip size="small" color="success" label="Active" /></Stack>
              <Typography color="text.secondary" mt={1}>{Number(item.OutputQuantity).toLocaleString()} units · Rs {Number(item.Cost).toLocaleString()}</Typography>
            </Box>
          ))}</Stack>
        </CardContent></Card></Grid>
      </Grid>
    </>
  );
}
